import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../api';
import Sidebar from './Sidebar';
import PartnerOnboardingView from './PartnerOnboardingView';
import OdometerView from './OdometerView';
import TransactionView from './TransactionView';
import JuspayLogsView from './JuspayLogsView';
import VehicleManagementView from './VehicleManagementView';
import MaintenanceView from './MaintenanceView';
import RmView from './RmView';
import CashCollectionView from './CashCollectionView';
import {
  Users, Truck, UserCheck, Calendar, Play, Route, TrendingUp,
  IndianRupee, CreditCard, Zap, UserCog, Filter, RotateCcw,
  Wallet, BarChart2, FileMinus, Activity, Sparkles, Building, ShieldCheck, Briefcase, TrendingDown, Wrench
} from 'lucide-react';
import Loader from './Loader';

// ===== Utilities =====
const formatCompact = (num) => {
  if (!num || isNaN(num)) return "0";
  if (num >= 1_00_00_000) return (num / 1_00_00_000).toFixed(1) + ' Cr';
  if (num >= 1_00_000) return (num / 1_00_000).toFixed(1) + ' Lakh';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const formatCurrencyCompact = (amount) => `₹${formatCompact(Math.round(amount))}`;
const formatNumber = (num) => formatCompact(num);

// ===== Section Wrapper =====
const Section = ({ title, icon, children }) => (
  <div className="bg-white dark:bg-neutral-800/50 p-6 sm:p-8 rounded-3xl shadow-lg border border-neutral-200/50 dark:border-neutral-700/50">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
        {React.cloneElement(icon, { className: "w-6 h-6 text-white" })}
      </div>
      <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">{title}</h2>
    </div>
    {children}
  </div>
);

// ===== Enhanced StatCard =====
const StatCard = ({ title, value, icon, color, className = "" }) => (
  <div className={`group relative bg-neutral-50 dark:bg-neutral-800 p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-neutral-200/50 dark:border-neutral-700/50 overflow-hidden ${className}`}>
    <div className="relative flex items-center gap-5">
      <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${color} shadow-lg`}>
        {React.cloneElement(icon, { className: "w-7 h-7 text-white" })}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400">{title}</p>
        <p className="text-2xl font-black text-neutral-900 dark:text-white tracking-tight">{value}</p>
      </div>
    </div>
  </div>
);

// ===== Date Filter =====
const DateFilter = ({ startDate, endDate, onStartDateChange, onEndDateChange, onReset, onApply, isLoading }) => (
  <div className="bg-gradient-to-r from-white to-neutral-50 dark:from-neutral-800 dark:to-neutral-900 p-6 sm:p-8 rounded-3xl shadow-xl border border-neutral-200/50 dark:border-neutral-700/50 mb-8">
    {isLoading && <Loader />}
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Filter className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-neutral-800 dark:text-white">Date Range Filter</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Select a range to filter dashboard data</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 block">Start Date</label>
          <input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} disabled={isLoading} className="px-4 py-3 border-2 border-neutral-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 block">End Date</label>
          <input type="date" value={endDate} onChange={(e) => onEndDateChange(e.target.value)} disabled={isLoading} className="px-4 py-3 border-2 border-neutral-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm" />
        </div>
        <div className="flex gap-3">
          <button onClick={onApply} disabled={isLoading || !startDate || !endDate} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 text-white rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
            {isLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Applying...</> : <><Activity className="w-4 h-4" />Apply</>}
          </button>
          <button onClick={onReset} disabled={isLoading} className="px-6 py-3 bg-gradient-to-r from-neutral-500 to-neutral-600 hover:from-neutral-600 hover:to-neutral-700 disabled:opacity-50 text-white rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
            <RotateCcw className="w-4 h-4" />Reset
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ===== Chart Components =====
const ChartContainer = ({ title, icon, children }) => (
  <div className="bg-neutral-50 dark:bg-neutral-800 p-6 rounded-2xl shadow-md border border-neutral-200/50 dark:border-neutral-700/50 h-full">
    <div className="flex items-center gap-3 mb-4">
      {React.cloneElement(icon, { className: "w-5 h-5 text-neutral-600 dark:text-neutral-300" })}
      <h3 className="text-lg font-bold text-neutral-800 dark:text-white">{title}</h3>
    </div>
    <div className="h-[300px]">
      {children}
    </div>
  </div>
);

const VehicleStatusChart = ({ data }) => {
  const COLORS = { Active: '#10b981', Down: '#ef4444', 'Temp Replacement': '#f59e0b' };
  return (
    <ChartContainer title="Vehicle Status" icon={<Truck />}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" labelLine={false} outerRadius="80%" dataKey="count" nameKey="status" strokeWidth={3} stroke="#ffffff">
            {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[entry.status] || '#8884d8'} />)}
          </Pie>
          <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

const PartnerActivityChart = ({ active, inactive }) => {
  const data = [{ name: 'Active', value: active }, { name: 'Inactive', value: inactive }];
  const COLORS = ['#10b981', '#ef4444'];
  return (
    <ChartContainer title="Partner Activity" icon={<Users />}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius="50%" outerRadius="80%" dataKey="value" nameKey="name" paddingAngle={5} strokeWidth={3} stroke="#ffffff">
            {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

const PnlChart = ({ revenue, collection, cost, profit }) => {
  const data = [
    { name: 'Revenue', value: revenue, fill: '#3b82f6' },
    { name: 'Collection', value: collection, fill: '#10b981' },
    { name: 'Costs', value: cost, fill: '#f59e0b' },
    { name: 'Profit', value: profit, fill: profit >= 0 ? '#22c55e' : '#ef4444' },
  ];
  return (
    <ChartContainer title="Profit & Loss" icon={profit >= 0 ? <TrendingUp /> : <TrendingDown />}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={formatCompact} />
          <YAxis type="category" dataKey="name" width={80} />
          <Tooltip cursor={{ fill: 'rgba(240, 240, 240, 0.3)'}} contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} formatter={(value) => formatCurrencyCompact(value)} />
          <Bar dataKey="value" barSize={30} radius={[0, 10, 10, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// ===== Recent Activity =====
const RecentActivity = ({ partners }) => (
  <div className="bg-neutral-50 dark:bg-neutral-800 p-6 rounded-2xl shadow-md border border-neutral-200/50 dark:border-neutral-700/50 h-full">
    <div className="flex items-center gap-3 mb-4">
      <Users className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
      <h3 className="text-lg font-bold text-neutral-800 dark:text-white">Recent Onboarding</h3>
    </div>
    <div className="space-y-3 max-h-[280px] overflow-y-auto custom-scrollbar">
      {partners?.length > 0 ? partners.map((p, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-neutral-700/50 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50"><Users className="w-5 h-5 text-blue-600 dark:text-blue-400" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-neutral-800 dark:text-white text-sm truncate">{p.partner_name}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{new Date(p.onboarding_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          </div>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        </div>
      )) : <div className="text-center py-10"><Users className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" /><p className="text-neutral-500 dark:text-neutral-400">No recent activity</p></div>}
    </div>
  </div>
);

// ===== Main Dashboard Component =====
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [maintenanceCost, setMaintenanceCost] = useState(null);

  const fetchStats = useCallback(async (start = null, end = null) => {
    try {
      setIsFiltering(true);
      setError(null);
      const params = new URLSearchParams();
      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);

      // Fetch stats
      const response = await api.get(`/dashboard/stats${params.toString() ? `?${params.toString()}` : ''}`);
      setStats(response.data);

      // Fetch maintenance cost separately
      try {
        const maintenanceRes = await api.get(`/dashboard/maintenance-cost${params.toString() ? `?${params.toString()}` : ''}`);
        setMaintenanceCost(maintenanceRes.data.totalCost || 0);
      } catch (maintenanceErr) {
        console.error("Failed to fetch maintenance cost:", maintenanceErr.response?.data || maintenanceErr.message);
        setMaintenanceCost(0); // Set to 0 on error to prevent crash
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch dashboard data');
    } finally {
      setIsFiltering(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading && !stats) return <div className="flex items-center justify-center min-h-screen"><div className="text-center"><div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div><p className="text-xl font-semibold text-neutral-600 dark:text-neutral-400">Loading Dashboard...</p></div></div>;
  if (error && !stats) return <div className="flex items-center justify-center min-h-screen"><div className="text-center p-8 bg-red-50 dark:bg-red-900/20 rounded-3xl border border-red-200 dark:border-red-800"><p className="text-xl font-semibold text-red-600 dark:text-red-400">{error}</p></div></div>;
  if (!stats) return <div className="flex items-center justify-center min-h-screen"><div className="text-center p-8 bg-neutral-50 dark:bg-neutral-800 rounded-3xl border border-neutral-200 dark:border-neutral-700"><p className="text-xl font-semibold text-neutral-600 dark:text-neutral-400">No data available</p></div></div>;

  const s = {
    totalPartners: stats.totalPartners || 0,
    activePartners: stats.activePartners || 0,
    inactivePartners: stats.inactivePartners || 0,
    totalVehicles: stats.totalVehicles || 0,
    activeVehicles: stats.activeVehicles || 0,
    totalRms: stats.totalRms || 0,
    vehiclesThisMonth: stats.vehiclesThisMonth || 0,
    totalKmThisMonth: stats.totalKmThisMonth || 0,
    averageKmThisMonth: stats.averageKmThisMonth || 0,
    totalAmountThisMonth: stats.totalAmountThisMonth || 0,
    totalEmiActiveVehicles: stats.totalEmiActiveVehicles || 0,
    totalChargingCostThisMonth: stats.totalChargingCostThisMonth || 0,
    vehicleStatus: stats.vehicleStatus || [],
    recentPartners: stats.recentPartners || [],
    dailyCollection: stats.dailyCollection || 0,
    totalCollection: stats.totalCollection || 0,
    totalDue: stats.totalDue || 0,
    statusOfCases: stats.statusOfCases || { vehicleReturned: 0, vehicleReturnReplaced: 0 },
  };

  const periodLabel = startDate && endDate ? `(${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()})` : '(This Month)';
  const manpowerCost = 100000; // hardcoded 1 lakh
  const totalCosts =
  s.totalEmiActiveVehicles +
  s.totalChargingCostThisMonth +
  (maintenanceCost || 0) +
  manpowerCost;
  const netProfit = s.totalAmountThisMonth - totalCosts;

  return (
    <div className="space-y-8 pb-8">
      <DateFilter startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onReset={() => { setStartDate(''); setEndDate(''); fetchStats(); }} onApply={() => fetchStats(startDate, endDate)} isLoading={isFiltering} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          <Section title="Fleet & Operations" icon={<Truck />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
              <StatCard title="Total Vehicles" value={formatNumber(s.totalVehicles)} icon={<Truck />} color="bg-gradient-to-br from-emerald-500 to-emerald-600" />
              <StatCard title="Active Vehicles" value={formatNumber(s.activeVehicles)} icon={<Play />} color="bg-gradient-to-br from-teal-500 to-teal-600" />
              <StatCard title={`Total KM ${periodLabel}`} value={`${formatNumber(s.totalKmThisMonth)} KM`} icon={<Route />} color="bg-gradient-to-br from-orange-500 to-orange-600" />
              <StatCard title={`Avg KM ${periodLabel}`} value={`${formatNumber(s.averageKmThisMonth)} KM`} icon={<TrendingUp />} color="bg-gradient-to-br from-cyan-500 to-cyan-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <VehicleStatusChart data={s.vehicleStatus} />
              <PnlChart revenue={s.totalAmountThisMonth} collection={s.totalCollection} cost={totalCosts} profit={netProfit} />
            </div>
          </Section>

          <Section title="Partner Network" icon={<Building />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
              <StatCard title="Total Partners" value={formatNumber(s.totalPartners)} icon={<Users />} color="bg-gradient-to-br from-blue-500 to-blue-600" />
              <StatCard title="Active Partners" value={formatNumber(s.activePartners)} icon={<UserCheck />} color="bg-gradient-to-br from-green-500 to-green-600" />
              <StatCard title="Inactive Partners" value={formatNumber(s.inactivePartners)} icon={<UserCog />} color="bg-gradient-to-br from-red-500 to-red-600" />
              <StatCard title="Total RMs" value={formatNumber(s.totalRms)} icon={<Briefcase />} color="bg-gradient-to-br from-indigo-500 to-indigo-600" />
            </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PartnerActivityChart active={s.activePartners} inactive={s.inactivePartners} />
              <RecentActivity partners={s.recentPartners} />
            </div>
          </Section>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-1 space-y-8">
          <Section title="Financials" icon={<IndianRupee />}>
            <div className="space-y-4">
              <StatCard title={`Total Revenue ${periodLabel}`} value={formatCurrencyCompact(s.totalAmountThisMonth)} icon={<IndianRupee />} color="bg-gradient-to-br from-rose-500 to-rose-600" />
              <StatCard title={`Total Collection ${periodLabel}`} value={formatCurrencyCompact(s.totalCollection)} icon={<Wallet />} color="bg-gradient-to-br from-green-500 to-green-600" />
              <StatCard title={`Total Due ${periodLabel}`} value={formatCurrencyCompact(s.totalDue)} icon={<FileMinus />} color="bg-gradient-to-br from-red-500 to-red-600" />
              <StatCard title="EMI (Active)" value={formatCurrencyCompact(s.totalEmiActiveVehicles)} icon={<CreditCard />} color="bg-gradient-to-br from-amber-500 to-amber-600" />
              <StatCard title={`Charging Cost ${periodLabel}`} value={formatCurrencyCompact(s.totalChargingCostThisMonth)} icon={<Zap />} color="bg-gradient-to-br from-yellow-500 to-yellow-600" />
              <StatCard title="Maintenance Cost" value={maintenanceCost !== null ? formatCurrencyCompact(maintenanceCost) : "N/A"} icon={<Wrench />} color="bg-gradient-to-br from-stone-500 to-stone-600" />
              <StatCard title="Manpower Cost" value={formatCurrencyCompact(manpowerCost)} icon={<UserCog />} color="bg-gradient-to-br from-slate-500 to-slate-600" />
            </div>
          </Section>
          <Section title="Case Status" icon={<ShieldCheck />}>
             <div className="space-y-4">
                <StatCard title="Vehicle Returned" value={formatNumber(s.statusOfCases.vehicleReturned)} icon={<Truck />} color="bg-gradient-to-br from-gray-500 to-gray-600" />
                <StatCard title="Return & Replaced" value={formatNumber(s.statusOfCases.vehicleReturnReplaced)} icon={<RotateCcw />} color="bg-gradient-to-br from-slate-500 to-slate-600" />
             </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

// ===== AdminPanel Wrapper =====
const AdminPanel = ({ onLogout, theme, toggleTheme }) => {
  const [selected, setSelected] = useState('dashboard');
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-950">
      <Sidebar selected={selected} onSelect={setSelected} isOpen={isSidebarOpen} setOpen={setSidebarOpen} onLogout={onLogout} theme={theme} toggleTheme={toggleTheme} />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto transition-all custom-scrollbar">
        <div className="w-full max-w-8xl mx-auto">
          {selected === 'dashboard' ? <Dashboard /> :
            selected === 'onboarding' ? <PartnerOnboardingView /> :
              selected === 'odometer' ? <OdometerView /> :
                selected === 'transactions' ? <TransactionView /> :
                  selected === 'juspay' ? <JuspayLogsView /> :
                    selected === 'vehicles' ? <VehicleManagementView /> :
                      selected === 'maintenance' ? <MaintenanceView /> :
                        selected === 'rm-onboarding' ? <RmView /> :
                          selected === 'cash-collection' ? <CashCollectionView /> : <Dashboard />}
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
